// brain.ts — the AI client. ONE billed call to the LIVE Oriora server brain:
//   POST https://api.orioralabs.com/api/apps/oriora_forever_paw/remember
// with the customer's Oriora ecosystem Bearer token. The server holds the prompt +
// bills the Oriora wallet (Managed AI / BYOK). Pet memories stay local; only a small
// DERIVED REMEMBRANCE SLICE (span, counts, milestone titles, recurring places, and a
// chronological sample of HER own short captions — see signals.rs) is sent for this
// ONE call. Photos and the full diary never leave the device.
//
// CRUD shape, not agentic: ONE text-in (scoped JSON) → text-out call. No agent loop,
// no second call, no tool-use chain, no direct OpenAI/Anthropic SDK in the app.
//
// HARD GUARDRAILS:
//   • GRIEF SOFT FENCE (server-enforced + mirrored here): the remembrance is COMFORTING
//     NARRATION of saved memories ONLY. Never grief counseling, therapy, or crisis
//     advice. No therapy claim.
//   • PRE-LLM REFUSAL FENCE (client): if the saved text shows acute self-harm / crisis
//     distress signals, we DO NOT call the model — we gently point to human support.
//   • CAN-only + OBSERVATIONAL: narrates the pet's own story as SHE saved it; never
//     prescriptive ("last vet visit 8 months ago", not "take her to the vet").

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { RemembranceSignals } from "./store";

const SERVER = "https://api.orioralabs.com";
const SLUG = "oriora_forever_paw";

export type BrainErrorCode =
  | "insufficient_funds"
  | "managed_ai_blocked"
  | "unauthenticated"
  | "distress_fence"
  | "unavailable"
  | "no_store"
  | "too_large";

export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// ── Pre-LLM distress fence ───────────────────────────────────────────────────
// This app comforts by narrating SAVED PET MEMORIES — it is not a crisis service.
// If the owner's own words carry acute self-harm / crisis signals, we must NOT route
// them into a "here's a warm story" AI reply; we gently surface human support instead.
// Deliberately narrow (acute self-harm phrasing only) so ordinary grief — which IS the
// point of this book — passes straight through. Whole-word, case-insensitive.
const DISTRESS_PATTERNS: RegExp[] = [
  /\bkill myself\b/i,
  /\bkilling myself\b/i,
  /\bend my life\b/i,
  /\bending my life\b/i,
  /\btake my (own )?life\b/i,
  /\bwant to die\b/i,
  /\bdon'?t want to (be alive|live)\b/i,
  /\bno reason to live\b/i,
  /\bcan'?t go on (without|any longer)\b/i,
  /\bsuicidal\b/i,
  /\bcommit suicide\b/i,
  /\bharm(?:ing)? myself\b/i,
  /\bhurt(?:ing)? myself\b/i,
  /\bend(?:ing)?\s+it\s+all\b/i,
  /\b(?:isn'?t|ain'?t|not|no longer)\s+worth\s+living\b/i,
  /\bnothing\s+to\s+live\s+for\b/i,
];

/** True if free text trips the acute-distress fence (shared pattern set). Used both on the
 *  computed remembrance slice (hasDistressSignals, below) AND — for the R8 chat graduation —
 *  on the raw chat message itself, pre-LLM, so the SAME safety line runs whichever surface
 *  the words come in on (the memory-book diary, or a typed chat message). */
export function hasDistressText(text: string): boolean {
  return DISTRESS_PATTERNS.some((re) => re.test(text));
}

/** True if the sampled, owner-written text trips the acute-distress fence. */
export function hasDistressSignals(signals: RemembranceSignals): boolean {
  const haystacks: string[] = [];
  for (const m of signals.sampled_moments) {
    if (m.title) haystacks.push(m.title);
    if (m.caption_excerpt) haystacks.push(m.caption_excerpt);
  }
  for (const t of signals.milestones.titles) {
    if (t.title) haystacks.push(t.title);
  }
  const blob = haystacks.join("\n").toLowerCase();
  return hasDistressText(blob);
}

/** The one shared distress-fence message — human support, never the model. */
export const DISTRESS_MESSAGE =
  "It sounds like you may be going through something very hard. Forever Paw keeps your pet's memories — it isn't crisis support. Please reach out to someone who can help right now: in the US call or text 988 (Suicide & Crisis Lifeline); elsewhere, find a local line at findahelpline.com.";

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(action: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/${SLUG}/${action}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "The story service is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "Add funds in your Oriora account to weave their story.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "The service is busy — please try again in a moment.");
    }
    if (!res.ok) throw new BrainError("unavailable", "The story request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "The story service is temporarily unavailable.");
}

export interface Remembrance {
  remembrance: string;
}

/**
 * Weave a remembrance — the ONE AI feature.
 *
 * We do NOT ship the full memory book. We compute the structure on-device (span,
 * counts, milestone titles, recurring places, a chronological sample of HER captions
 * — see signals.rs) and send ONLY that small slice. The brain then narrates THOSE
 * specific saved moments — the pet's own story — in a few warm sentences.
 *
 * The server prompt (operator task) reads the `signals` object and stays inside the
 * GRIEF SOFT FENCE: comforting narration of saved memories only — never grief
 * counseling, therapy, diagnosis, or crisis advice; no therapy claim. It names the
 * pre-computed figures ("across 11 years", "200 ordinary mornings"), never recomputes.
 */
export async function weaveRemembrance(signals: RemembranceSignals): Promise<Remembrance> {
  if (signals.overall.total_moments === 0) {
    throw new BrainError("unavailable", "Save a few moments first — there's nothing to remember yet.");
  }
  // PRE-LLM fence: acute crisis signals → do not call the model; route to human support.
  if (hasDistressSignals(signals)) {
    throw new BrainError("distress_fence", DISTRESS_MESSAGE);
  }
  const r = await brainCall<Partial<Remembrance>>("remember", { signals });
  return {
    remembrance: String(r.remembrance ?? "").trim(),
  };
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds a FAITHFUL backup locally (export_backup); these relay it to the user's OWN
// connected cloud store — their GitHub repo or Google Drive (connected once in the Oriora
// account). Oriora RETAINS nothing. Plain HTTP → works on iOS. Endpoints: the generic
// POST/GET /api/apps/oriora_forever_paw/backup (shared server route).
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/${SLUG}/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN cloud
 *  store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora retains
 *  nothing (text relay only). Throws no_store when nothing is connected. Endpoint: the shared
 *  POST /api/apps/oriora_forever_paw/store route (distinct from the whole-collection `backup` route). */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the
// OS browser — the in-app https handback is robust on iOS where the custom-scheme
// deep-link is fragile (mirrors the proven template). Session returns via
// auth:deep-link (App.tsx + ipc.rs). The web callback allowlists `oriora-forever-paw`
// as a return target (server-side, operator task).
export const openWebLogin = () => invoke<void>("open_login_window");
